import { ModelDataType } from 'src/app/interfaces/model-data-type';
import { ServiceDistance } from 'src/app/interfaces/service-distance';
import { ViveSubStatuses } from 'src/app/interfaces/vive-sub-statuses';

import { SignedDocument } from '../../classes/models/signed-document';
import { CompanyVendorFile } from '../../interfaces/models/company-vendor-file';
import { RegistrationProgress } from '../../interfaces/models/registration-progress';
import { ViveStatuses } from '../../interfaces/vive-statuses';
import { BaseModel } from './base-model';
import { CanadianWorkersCompensation } from './canadian-workers-compensation';
import { ChangeHistory } from './change-history';
import { ClientRegisteredVendor } from './client-registered-vendor';
import { Company } from './company';
import { CompanyComplianceType } from './company-compliance-type';
import { CompanyVendorOffice } from './company-vendor-office';
import { CompanyVendorPayment } from './company-vendor-payment';
import { ExemptVendor } from './exempt-vendor';
import { InsuranceCertificate } from './insurance-certificate';
import { Vendor } from './vendor';
import { ViveProgress } from './vive-progress';

export class CompanyVendor extends BaseModel {
  static override columnMap = new Map<
    string,
    { property: string; type: 'column'; dataType: ModelDataType }
  >();
  static override associationMap = new Map<
    string,
    { property: string; type: 'association'; model: string }
  >();
  static override aliasedColumnMap = new Map<
    string,
    { property: string; type: 'alias' }
  >();

  static CompanyVendorProperties: (
    | {
        property: keyof CompanyVendor;
        type: 'column';
        dataType: ModelDataType;
      }
    | {
        property: keyof CompanyVendor;
        type: 'alias';
      }
    | {
        property: keyof CompanyVendor;
        type: 'association';
        model: string;
      }
  )[] = [
    { property: 'id', type: 'column', dataType: 'number' },
    { property: 'CompanyId', type: 'column', dataType: 'number' },
    { property: 'VendorId', type: 'column', dataType: 'number' },
    { property: 'ExemptVendorId', type: 'column', dataType: 'number' },
    {
      property: 'ClientRegisteredVendorId',
      type: 'column',
      dataType: 'number'
    },
    { property: 'viveStatus', type: 'column', dataType: 'string' },
    { property: 'viveSubStatus', type: 'column', dataType: 'string' },
    { property: 'resignRequired', type: 'column', dataType: 'date' },
    { property: 'statusDate', type: 'column', dataType: 'date' },
    { property: 'vendorPaid', type: 'column', dataType: 'boolean' },
    { property: 'feeWaived', type: 'column', dataType: 'boolean' },
    { property: 'feeWaivedDate', type: 'column', dataType: 'date' },
    { property: 'needsFeeWaiveRenewal', type: 'column', dataType: 'boolean' },
    { property: 'isUnFeeWaived', type: 'column', dataType: 'boolean' },
    { property: 'unFeeWaivedDate', type: 'column', dataType: 'date' },
    { property: 'vendorLastDatePaid', type: 'column', dataType: 'date' },
    { property: 'glIsNamed', type: 'column', dataType: 'boolean' },
    { property: 'autoIsNamed', type: 'column', dataType: 'boolean' },
    { property: 'umbIsName', type: 'column', dataType: 'boolean' },
    { property: 'wkIsNamed', type: 'column', dataType: 'boolean' },
    { property: 'errIsNamed', type: 'column', dataType: 'boolean' },
    { property: 'tradeIsNamed', type: 'column', dataType: 'boolean' },
    { property: 'glDescOps', type: 'column', dataType: 'boolean' },
    { property: 'autoDescOps', type: 'column', dataType: 'boolean' },
    { property: 'umbDescOps', type: 'column', dataType: 'boolean' },
    { property: 'wkDescOps', type: 'column', dataType: 'boolean' },
    { property: 'errDescOps', type: 'column', dataType: 'boolean' },
    { property: 'tradeDescOps', type: 'column', dataType: 'boolean' },
    { property: 'glIsAgency', type: 'column', dataType: 'boolean' },
    { property: 'autoIsAgency', type: 'column', dataType: 'boolean' },
    { property: 'umbIsAgency', type: 'column', dataType: 'boolean' },
    { property: 'wkIsAgency', type: 'column', dataType: 'boolean' },
    { property: 'errIsAgency', type: 'column', dataType: 'boolean' },
    { property: 'tradeIsAgency', type: 'column', dataType: 'boolean' },
    { property: 'overallVendorRating', type: 'column', dataType: 'number' },
    { property: 'CompanyRankingId', type: 'column', dataType: 'number' },
    { property: 'transitionDate', type: 'column', dataType: 'date' },
    { property: 'isTransitional', type: 'column', dataType: 'boolean' },
    { property: 'transitionTrades', type: 'column', dataType: 'string' },
    { property: 'country', type: 'column', dataType: 'string' },
    { property: 'CAWCNumber', type: 'column', dataType: 'string' },
    { property: 'CAWCcertNumber', type: 'column', dataType: 'string' },
    { property: 'CAWCStatus', type: 'column', dataType: 'string' },
    { property: 'CAWCDate', type: 'column', dataType: 'date' },
    { property: 'CAWCExempt', type: 'column', dataType: 'boolean' },
    { property: 'issue1099', type: 'column', dataType: 'boolean' },
    {
      property: 'lastRenewalAnniversaryDate',
      type: 'column',
      dataType: 'date'
    },
    { property: 'allowEinAutoLink', type: 'column', dataType: 'boolean' },
    { property: 'isArchived', type: 'column', dataType: 'boolean' },
    {
      property: 'isUsingNewBusinessVerification',
      type: 'column',
      dataType: 'boolean'
    },
    { property: 'isWCExemptValid', type: 'column', dataType: 'boolean' },
    { property: 'isWorkersCompExempt', type: 'column', dataType: 'boolean' },
    { property: 'lastFeeWaivedDate', type: 'column', dataType: 'date' },
    { property: 'useTransporterTrades', type: 'column', dataType: 'boolean' },
    { property: 'isTollBrothers', type: 'column', dataType: 'boolean' },
    {
      property: 'isBusinessRequirementsConfirmed',
      type: 'column',
      dataType: 'boolean'
    },
    {
      property: 'isOwnerRequirementsConfirmed',
      type: 'column',
      dataType: 'boolean'
    },
    {
      property: 'isInsuranceRequirementsConfirmed',
      type: 'column',
      dataType: 'boolean'
    },
    {
      property: 'isVendorAgreementConfirmed',
      type: 'column',
      dataType: 'boolean'
    },
    {
      property: 'vendorAgreementConfirmedDate',
      type: 'column',
      dataType: 'date'
    },
    { property: 'CompanyComplianceTypeId', type: 'column', dataType: 'number' },
    { property: 'BundleId', type: 'column', dataType: 'number' },
    { property: 'forceTempActive', type: 'column', dataType: 'boolean' },
    { property: 'lastForceTempActiveDate', type: 'column', dataType: 'date' },
    { property: 'forceActiveCounter', type: 'column', dataType: 'number' },
    { property: 'createdAt', type: 'column', dataType: 'date' },
    { property: 'updatedAt', type: 'column', dataType: 'date' },

    { property: 'vendorType', type: 'alias' },
    { property: 'sharedVendorId', type: 'alias' },
    { property: 'vendorLegalName', type: 'alias' },
    { property: 'vendorDba', type: 'alias' },
    { property: 'vendorStreetAddress1', type: 'alias' },
    { property: 'vendorStreetAddress2', type: 'alias' },
    { property: 'vendorStreetState', type: 'alias' },
    { property: 'vendorStreetCity', type: 'alias' },
    { property: 'vendorStreetZip', type: 'alias' },
    { property: 'vendorMailingAddress1', type: 'alias' },
    { property: 'vendorMailingAddress2', type: 'alias' },
    { property: 'vendorMailingState', type: 'alias' },
    { property: 'vendorMailingCity', type: 'alias' },
    { property: 'vendorMailingZip', type: 'alias' },
    { property: 'vendorRemitToAddress1', type: 'alias' },
    { property: 'vendorRemitToAddress2', type: 'alias' },
    { property: 'vendorRemitToCity', type: 'alias' },
    { property: 'vendorRemitToState', type: 'alias' },
    { property: 'vendorRemitToZip', type: 'alias' },
    { property: 'vendorSiteId', type: 'alias' },
    { property: 'hasVendorPaidForAnyClient', type: 'alias' },
    { property: 'allPaidClientOperationStates', type: 'alias' },
    { property: 'allClientOperationStates', type: 'alias' },
    { property: 'isOwnerInfoRequired', type: 'alias' },
    { property: 'hasMultipleClients', type: 'alias' },
    { property: 'renewalDate', type: 'alias' },
    { property: 'infoRenewalDueDate', type: 'alias' },
    { property: 'businessInfoReview', type: 'alias' },
    { property: 'ownerInfoReview', type: 'alias' },
    { property: 'tradeReview', type: 'alias' },
    { property: 'insuranceReview', type: 'alias' },
    { property: 'finalReview', type: 'alias' },
    { property: 'feeVouchersLeft', type: 'alias' },
    { property: 'vendorTradeCategories', type: 'alias' },
    { property: 'vendorTrades', type: 'alias' },
    { property: 'vendorRank', type: 'alias' },
    { property: 'vendorRating', type: 'alias' },
    { property: 'vendorEin', type: 'alias' },
    { property: 'vendorAccountingKeys', type: 'alias' },
    { property: 'vendorSiteKeys', type: 'alias' },
    { property: 'vendorSiteNames', type: 'alias' },
    { property: 'viveReviewsInProgress', type: 'alias' },
    { property: 'isApLinked', type: 'alias' },
    { property: 'officeLocations', type: 'alias' },
    { property: 'vendorCompanyType', type: 'alias' },
    { property: 'vendorLlcType', type: 'alias' },
    { property: 'vendorBidContactName', type: 'alias' },
    { property: 'vendorBidContactEmail', type: 'alias' },
    { property: 'vendorContactName', type: 'alias' },
    { property: 'vendorContactEmail', type: 'alias' },
    { property: 'vendorContactPhone', type: 'alias' },
    { property: 'vendorContactPhone2', type: 'alias' },
    { property: 'vendorFax', type: 'alias' },
    { property: 'vendorPhone', type: 'alias' },
    { property: 'nextExpiringInsuranceDate', type: 'alias' },
    { property: 'vendorPriorityLevel', type: 'alias' },
    { property: 'clientSpecificVendorRating', type: 'alias' },
    { property: 'hasSignedVendorAgreement', type: 'alias' },
    { property: 'serviceArea', type: 'alias' },
    { property: 'hasLicenseRequirement', type: 'alias' },
    { property: 'vendorRiskLevel', type: 'alias' },
    { property: 'vendorComplianceType', type: 'alias' },
    { property: 'vendorBsn', type: 'alias' },
    { property: 'wsibStatus', type: 'alias' },
    { property: 'wsibExpirationDate', type: 'alias' },
    { property: 'wcbStatus', type: 'alias' },
    { property: 'wcbExpirationDate', type: 'alias' },
    { property: 'complianceContactName', type: 'alias' },
    { property: 'complianceContactEmail', type: 'alias' },
    { property: 'complianceContactPhone1', type: 'alias' },
    { property: 'lastPaymentDate', type: 'alias' },
    { property: 'lastPaymentType', type: 'alias' },
    { property: 'vendorYearEstablished', type: 'alias' },
    { property: 'vendorQtyEmployees', type: 'alias' },
    { property: 'vendorFeeWaived', type: 'alias' },
    { property: 'vendorFeeWaivedDate', type: 'alias' },
    { property: 'exemptVendorType', type: 'alias' },
    { property: 'packageType', type: 'alias' },
    { property: 'vendorTradeStates', type: 'alias' },
    { property: 'isFeeWaivedAndNeedsRenewal', type: 'alias' },
    { property: 'needsToRenew', type: 'alias' },
    { property: 'hasRenewalProgress', type: 'alias' },
    { property: 'currentRegistrationStep', type: 'alias' },
    { property: 'wasFeeWaived', type: 'alias' },
    { property: 'hasCompletedRegistration', type: 'alias' },
    { property: 'clientVendorSharedStates', type: 'alias' },
    { property: 'avg(daysInReviewingStatus)', type: 'alias' },
    {
      property: 'count(businessInfoReviewUnstartedOrReviewing)',
      type: 'alias'
    },
    {
      property: 'count(insuranceInfoReviewUnstartedOrReviewing)',
      type: 'alias'
    },
    { property: 'count(ownerInfoReviewUnstartedOrReviewing)', type: 'alias' },
    { property: 'count(tradeInfoReviewUnstartedOrReviewing)', type: 'alias' },

    {
      property: 'count(businessInfoReviewUnstartedOrRenewing)',
      type: 'alias'
    },
    {
      property: 'count(insuranceInfoReviewUnstartedOrRenewing)',
      type: 'alias'
    },
    { property: 'count(ownerInfoReviewUnstartedOrRenewing)', type: 'alias' },
    { property: 'count(tradeInfoReviewUnstartedOrRenewing)', type: 'alias' },
    { property: 'count(upcomingRenewal30Days)', type: 'alias' },
    { property: 'count(upcomingRenewal60Days)', type: 'alias' },
    { property: 'count(upcomingRenewal90Days)', type: 'alias' },
    { property: 'nonRenewingLast30Days', type: 'alias' },
    { property: 'nonRenewingLast60Days', type: 'alias' },
    { property: 'nonRenewingLast90Days', type: 'alias' },
    { property: 'spotlightVendorPriorityLevel', type: 'alias' },
    { property: 'vendorEinOrBsn', type: 'alias' },
    { property: 'vendorApprovedTradeStates', type: 'alias' },

    { property: 'Company', model: 'Company', type: 'association' },
    { property: 'Vendor', model: 'Vendor', type: 'association' },
    { property: 'ExemptVendor', model: 'ExemptVendor', type: 'association' },
    {
      property: 'ClientRegisteredVendor',
      model: 'ClientRegisteredVendor',
      type: 'association'
    },
    {
      property: 'CompanyVendorFiles',
      model: 'CompanyVendorFile',
      type: 'association'
    },
    {
      property: 'CompanyVendorOffices',
      model: 'CompanyVendorOffice',
      type: 'association'
    },
    {
      property: 'CompanyComplianceType',
      model: 'CompanyComplianceType',
      type: 'association'
    },
    {
      property: 'RegistrationProgresses',
      model: 'RegistrationProgress',
      type: 'association'
    },
    {
      property: 'SignedDocuments',
      model: 'SignedDocument',
      type: 'association'
    },
    {
      property: 'CompanyVendorPayments',
      model: 'CompanyVendorPayment',
      type: 'association'
    },
    {
      property: 'RecentPaidCompanyVendorPayment',
      model: 'RecentPaidCompanyVendorPayment',
      type: 'association'
    },
    {
      property: 'RecentPendingOrPaidCompanyVendorPayment',
      model: 'RecentPendingOrPaidCompanyVendorPayment',
      type: 'association'
    },
    {
      property: 'RecentCompanyVendorPayment',
      model: 'RecentCompanyVendorPayment',
      type: 'association'
    },
    {
      property: 'ChangeHistories',
      model: 'ChangeHistory',
      type: 'association'
    },
    {
      property: 'InsuranceCertificates',
      model: 'InsuranceCertificate',
      type: 'association'
    },
    {
      property: 'CanadianWorkersCompensations',
      model: 'CanadianWorkersCompensation',
      type: 'association'
    },
    { property: 'WSIB', model: 'WSIB', type: 'association' },
    { property: 'WCB', model: 'WCB', type: 'association' },
    {
      property: 'BusinessComplianceProgress',
      model: 'BusinessComplianceProgress',
      type: 'association'
    },
    {
      property: 'OwnerComplianceProgress',
      model: 'OwnerComplianceProgress',
      type: 'association'
    },
    {
      property: 'TradeLicenseComplianceProgress',
      model: 'TradeLicenseComplianceProgress',
      type: 'association'
    },
    {
      property: 'InsuranceComplianceProgress',
      model: 'InsuranceComplianceProgress',
      type: 'association'
    }
  ];

  static {
    CompanyVendor.CompanyVendorProperties.forEach((propertyObject) => {
      const { property, type } = propertyObject;
      if (type === 'column') {
        CompanyVendor.columnMap.set(property, propertyObject);
      }
      if (type === 'alias') {
        CompanyVendor.aliasedColumnMap.set(
          property,
          propertyObject as { property: string; type: 'alias' }
        );
      }
      if (type === 'association') {
        CompanyVendor.associationMap.set(
          property,
          propertyObject as {
            property: string;
            type: 'association';
            model: string;
          }
        );
      }
    });
  }

  constructor(CompanyVendorObject: Partial<CompanyVendor>) {
    super();

    CompanyVendor.CompanyVendorProperties.forEach((propertyObject) => {
      const { property } = propertyObject;
      const value = CompanyVendorObject[property];
      if (value !== undefined) {
        Object.assign(this, { [property]: value });
      }
    });
  }

  id?: number;
  CompanyId?: number | null;
  VendorId?: number | null;
  ExemptVendorId?: number | null;
  ClientRegisteredVendorId?: number | null;
  viveStatus?: ViveStatuses;
  viveSubStatus?: ViveSubStatuses | null;
  resignRequired?: Date | null;
  statusDate?: Date;
  vendorPaid?: boolean;
  feeWaived?: boolean;
  feeWaivedDate?: Date | null;
  needsFeeWaiveRenewal?: boolean;
  isUnFeeWaived?: boolean;
  unFeeWaivedDate?: Date | null;
  vendorLastDatePaid?: Date | null;
  glIsNamed?: boolean;
  autoIsNamed?: boolean;
  umbIsName?: boolean;
  wkIsNamed?: boolean;
  errIsNamed?: boolean;
  tradeIsNamed?: boolean;
  glDescOps?: boolean;
  autoDescOps?: boolean;
  umbDescOps?: boolean;
  wkDescOps?: boolean;
  errDescOps?: boolean;
  tradeDescOps?: boolean;
  glIsAgency?: boolean;
  autoIsAgency?: boolean;
  umbIsAgency?: boolean;
  wkIsAgency?: boolean;
  errIsAgency?: boolean;
  tradeIsAgency?: boolean;
  overallVendorRating?: number | null;
  CompanyRankingId?: number;
  transitionDate?: Date | null;
  isTransitional?: boolean;
  transitionTrades?: string | null;
  country?: 'US' | 'CA';
  CAWCNumber?: string | null;
  CAWCcertNumber?: string | null;
  CAWCStatus?: 'Eligible' | 'Ineligible' | 'Preliminary' | null;
  CAWCDate?: Date | null;
  CAWCExempt?: boolean | null;
  issue1099?: boolean;
  lastRenewalAnniversaryDate?: Date | null;
  allowEinAutoLink?: boolean;
  isArchived?: boolean;
  isUsingNewBusinessVerification?: boolean;
  isWCExemptValid?: boolean | null;
  isWorkersCompExempt?: boolean;
  lastFeeWaivedDate?: Date | null;
  useTransporterTrades?: boolean;
  isTollBrothers?: boolean;
  isBusinessRequirementsConfirmed?: boolean;
  isOwnerRequirementsConfirmed?: boolean;
  isInsuranceRequirementsConfirmed?: boolean;
  isVendorAgreementConfirmed?: boolean;
  vendorAgreementConfirmedDate?: Date | null;
  CompanyComplianceTypeId?: number;
  BundleId?: number | null;
  forceTempActive?: boolean;
  lastForceTempActiveDate?: Date | null;
  forceActiveCounter?: number;
  createdAt?: Date;
  updatedAt?: Date;

  // These represent aliased columns, not actual fields on the CompanyVendor model.
  vendorType?: string;
  vendorPriorityLevel?: string;
  sharedVendorId?: string;
  vendorLegalName?: string;
  vendorDba?: string;
  vendorStreetAddress1?: string;
  vendorStreetAddress2?: string;
  vendorStreetState?: string;
  vendorStreetCity?: string;
  vendorStreetZip?: string;
  vendorMailingAddress1?: string;
  vendorMailingAddress2?: string;
  vendorMailingState?: string;
  vendorMailingCity?: string;
  vendorMailingZip?: string;
  vendorRemitToAddress1?: string;
  vendorRemitToAddress2?: string;
  vendorRemitToCity?: string;
  vendorRemitToState?: string;
  vendorRemitToZip?: string;
  vendorSiteId?: number | null;
  hasVendorPaidForAnyClient?: boolean | 1 | 0;
  allPaidClientOperationStates?: string;
  allClientOperationStates?: string;
  isOwnerInfoRequired?: boolean | 1 | 0;
  hasMultipleClients?: boolean | 1 | 0;
  // The PAYMENT renewal date for this one client relationship.
  renewalDate?: Date;
  // VPDW-4240: the vendor-level INFORMATION renewal due date — one date per
  // Vendor, identical across all of their client relationships. A different
  // clock from `renewalDate` above, with its own suspension chain.
  infoRenewalDueDate?: Date | null;
  businessInfoReview?: string;
  ownerInfoReview?: string;
  tradeReview?: string;
  insuranceReview?: string;
  finalReview?: string;
  feeVouchersLeft?: number;
  vendorTradeCategories?: string;
  vendorTrades?: string;
  vendorRank?: string;
  vendorRating?: string;
  vendorEin?: string;
  vendorAccountingKeys?: string;
  vendorSiteKeys?: string;
  vendorSiteNames?: string;
  viveReviewsInProgress?: string;
  isApLinked?: string;
  officeLocations?: string;
  vendorCompanyType?: string;
  vendorLlcType?: string;
  vendorBidContactName?: string;
  vendorBidContactEmail?: string;
  vendorContactName?: string;
  vendorContactEmail?: string;
  vendorContactPhone?: string;
  vendorContactPhone2?: string;
  vendorFax?: string;
  vendorPhone?: string;
  nextExpiringInsuranceDate?: Date;
  clientSpecificVendorRating?: number;
  hasSignedVendorAgreement?: boolean;
  registrationType?: 'registration' | 'renewal';
  serviceArea?: ServiceDistance;
  hasLicenseRequirement?: boolean;
  vendorRiskLevel?: string;
  vendorComplianceType?: string;
  vendorBsn?: string;
  wsibStatus?: string;
  wsibExpirationDate?: Date;
  wcbStatus?: string;
  wcbExpirationDate?: Date;
  complianceContactName?: string;
  complianceContactEmail?: string;
  complianceContactPhone1?: string;
  lastPaymentDate?: Date | null;
  lastPaymentType?: string;
  vendorYearEstablished?: string;
  vendorQtyEmployees?: string;
  vendorFeeWaived?: string;
  vendorFeeWaivedDate?: Date | null;
  exemptVendorType?: string;
  packageType?: 'FULL' | 'BASE';
  vendorTradeStates?: string;
  isFeeWaivedAndNeedsRenewal?: boolean;
  needsToRenew?: boolean;
  hasRenewalProgress?: boolean;
  currentRegistrationStep?: string;
  wasFeeWaived?: boolean;
  hasCompletedRegistration?: boolean;
  clientVendorSharedStates?: string;
  'avg(daysInReviewingStatus)'?: number;
  'avg(daysInRenewingStatus)'?: number;
  'count(businessInfoReviewUnstartedOrReviewing)'?: number;
  'count(insuranceInfoReviewUnstartedOrReviewing)'?: number;
  'count(ownerInfoReviewUnstartedOrReviewing)'?: number;
  'count(tradeInfoReviewUnstartedOrReviewing)'?: number;
  'count(businessInfoReviewUnstartedOrRenewing)'?: number;
  'count(insuranceInfoReviewUnstartedOrRenewing)'?: number;
  'count(ownerInfoReviewUnstartedOrRenewing)'?: number;
  'count(tradeInfoReviewUnstartedOrRenewing)'?: number;
  'count(upcomingRenewal30Days)'?: number;
  'count(upcomingRenewal60Days)'?: number;
  'count(upcomingRenewal90Days)'?: number;
  'nonRenewingLast30Days'?: number;
  'nonRenewingLast60Days'?: number;
  'nonRenewingLast90Days'?: number;
  spotlightVendorPriorityLevel?: boolean;
  vendorEinOrBsn?: string;
  vendorApprovedTradeStates?: string;

  Company?: Company;
  Vendor?: Vendor;
  ExemptVendor?: ExemptVendor;
  ClientRegisteredVendor?: ClientRegisteredVendor;
  CompanyVendorFiles?: CompanyVendorFile[];
  CompanyVendorOffices?: CompanyVendorOffice[];
  CompanyComplianceType?: CompanyComplianceType;
  RegistrationProgresses?: RegistrationProgress[];
  SignedDocuments?: SignedDocument[];
  CompanyVendorPayments?: CompanyVendorPayment[];
  RecentPaidCompanyVendorPayment?: CompanyVendorPayment;
  RecentPendingOrPaidCompanyVendorPayment?: CompanyVendorPayment;
  RecentCompanyVendorPayment?: CompanyVendorPayment;
  ChangeHistories?: ChangeHistory[];
  InsuranceCertificates?: InsuranceCertificate[];
  GLInsuranceCertificate?: InsuranceCertificate;
  AUTOInsuranceCertificate?: InsuranceCertificate;
  UMBInsuranceCertificate?: InsuranceCertificate;
  WCInsuranceCertificate?: InsuranceCertificate;
  PLInsuranceCertificate?: InsuranceCertificate;
  CanadianWorkersCompensations?: CanadianWorkersCompensation[];
  WSIB?: CanadianWorkersCompensation;
  WCB?: CanadianWorkersCompensation;
  BusinessComplianceProgress?: ViveProgress;
  OwnerComplianceProgress?: ViveProgress;
  TradeLicenseComplianceProgress?: ViveProgress;
  InsuranceComplianceProgress?: ViveProgress;
  ViveProgresses?: ViveProgress[];
}
